import { useState, useEffect, useCallback, useRef } from 'react';
import {
  AlertTriangle,
  Shield,
  Flame,
  Zap,
  Car,
  HelpCircle,
  X,
  CheckCircle,
  Loader2,
  Clock,
  User,
  ChevronRight,
  Phone,
  Stethoscope,
  Building2,
  ArrowDownUp,
  Info,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { accessApi } from '../services/accessApi';
import { emergencyNumbersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { enablePushNotifications } from '../lib/push';
import { formatDateTime } from '../lib/utils';

// Lane D panic rework (kimi plan 2026-10-09): 'ascensor' maps to the backend
// kind 'stuck_elevator' (POST /api/panic/trigger { kind, unitId?, condoId?, alertType }).
type AlertType = 'robo' | 'incendio' | 'apagon' | 'ascensor' | 'accidente' | 'otro';

interface AlertItem {
  id: string;
  alertType: AlertType;
  status: 'active' | 'attending' | 'responded' | 'resolved';
  notes?: string;
  createdAt: string;
  respondedAt?: string;
  resolvedAt?: string;
  userName?: string;
  unit?: string;
  resolution?: string;
}

const statusLabels: Record<string, string> = {
  active: 'Activa',
  // Backend uses 'attending'; UI also accepts the legacy 'responded'.
  attending: 'En atención',
  responded: 'En atención',
  resolved: 'Resuelta',
};

// Dark-mode contrast (Lane D): every light pair gets a dark: variant.
const statusColors: Record<string, string> = {
  active: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',
  attending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300',
  responded: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-300',
  resolved: 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300',
};

interface EmergencyContact {
  id: string;
  name: string;
  phone: string;
  category?: string;
}

// Visual config per emergency-contact category. Falls back to a generic icon
// for any category the backend returns that we don't explicitly know about.
const contactCategoryConfig: Record<string, { label: string; icon: typeof Shield; color: string; bg: string }> = {
  emergencia: { label: 'Emergencias', icon: AlertTriangle, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-500/20' },
  policia: { label: 'Policía', icon: Shield, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-500/20' },
  bomberos: { label: 'Bomberos', icon: Flame, color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-100 dark:bg-orange-500/20' },
  ambulancia: { label: 'Ambulancia', icon: Stethoscope, color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-100 dark:bg-rose-500/20' },
  salud: { label: 'Salud', icon: Stethoscope, color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-100 dark:bg-rose-500/20' },
  seguridad: { label: 'Seguridad interna', icon: Building2, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-500/20' },
  servicios: { label: 'Servicios', icon: Phone, color: 'text-slate-600 dark:text-slate-300', bg: 'bg-slate-100 dark:bg-slate-500/20' },
  otros: { label: 'Otros', icon: HelpCircle, color: 'text-slate-600 dark:text-slate-300', bg: 'bg-slate-100 dark:bg-slate-500/20' },
};

const getContactConfig = (category?: string) =>
  (category && contactCategoryConfig[category]) || {
    label: category || 'Contacto',
    icon: Phone,
    color: 'text-slate-600 dark:text-slate-300',
    bg: 'bg-slate-100 dark:bg-slate-500/20',
  };

// Normalize a raw alert (snake_case from the API) into the AlertItem shape
// the UI expects, so timestamps and types render correctly.
function normalizeAlert(raw: any): AlertItem {
  return {
    id: String(raw?.id ?? ''),
    alertType: (raw?.alertType ?? raw?.alert_type ?? 'otro') as AlertType,
    status: (raw?.status ?? 'active') as AlertItem['status'],
    notes: raw?.notes ?? undefined,
    createdAt: raw?.createdAt ?? raw?.created_at ?? '',
    respondedAt: raw?.respondedAt ?? raw?.responded_at ?? undefined,
    resolvedAt: raw?.resolvedAt ?? raw?.resolved_at ?? undefined,
    userName: raw?.userName ?? raw?.user_name ?? raw?.name ?? undefined,
    unit: raw?.unit ?? raw?.unit_label ?? undefined,
    resolution: raw?.resolution ?? undefined,
  };
}

export default function PanicButton() {
  const { t } = useTranslation();
  const { user, contexts, activeContextId, roles } = useAuth();
  const isAdminOrSecurity = user && ['admin', 'super_admin', 'security', 'directive'].includes(user.role);

  const [stage, setStage] = useState<'idle' | 'select' | 'sending' | 'sent'>('idle');
  const [selectedType, setSelectedType] = useState<AlertType | null>(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Presser feedback (Lane D): notified count from the 201 { alert, notified }
  // response, plus an informational toast for PANIC_DUPLICATE (409).
  const [notifiedCount, setNotifiedCount] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Active-alert list. Residents rely on it for the attending banner; the
  // /panic/active endpoint returns unit-scoped alerts for residents.
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);

  // Emergency contacts (shown to everyone)
  const [contacts, setContacts] = useState<EmergencyContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  // i18n-aware type config (ascensor label comes from the panic.* bundle keys;
  // legacy labels keep their hardcoded defaults).
  const alertTypes: { value: AlertType; label: string; icon: typeof Shield; color: string; bg: string }[] = [
    { value: 'robo', label: 'Robo', icon: Shield, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-500/20' },
    { value: 'incendio', label: 'Incendio', icon: Flame, color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-100 dark:bg-orange-500/20' },
    { value: 'apagon', label: 'Apagón', icon: Zap, color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-100 dark:bg-yellow-500/20' },
    { value: 'ascensor', label: t('panic.ascensor', 'Ascensor atascado'), icon: ArrowDownUp, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-500/20' },
    { value: 'accidente', label: 'Accidente', icon: Car, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-500/20' },
    { value: 'otro', label: 'Otro', icon: HelpCircle, color: 'text-slate-600 dark:text-slate-300', bg: 'bg-slate-100 dark:bg-slate-500/20' },
  ];

  const showToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  // Lane D (4): make sure a push subscription exists for this device so the
  // panic fan-out reaches the resident even with the tab closed. Silent
  // best-effort: any failure (no VAPID key, denied permission) is non-fatal.
  useEffect(() => {
    enablePushNotifications().catch(() => { /* best-effort */ });
  }, []);

  const fetchAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const res = await accessApi.panic.active();
      const payload = res.data?.data ?? res.data;
      const list = payload?.alerts || payload?.data?.alerts || (Array.isArray(payload) ? payload : []);
      setAlerts(Array.isArray(list) ? list.map(normalizeAlert) : []);
    } catch {
      setAlerts([]);
    } finally {
      setAlertsLoading(false);
    }
  }, []);

  const fetchContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = userData.condoId || (user as any)?.condoId || (user as any)?.condo_id;
      if (!condoId) {
        setContacts([]);
        return;
      }
      const res = await emergencyNumbersAPI.getAll(condoId);
      // Backend responds with { success, data: rows }; axios wraps that in
      const payload = res.data?.data ?? res.data;
      const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
            ? payload.items
            : [];
      setContacts(list as EmergencyContact[]);
    } catch {
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchAlerts();
    // Existing /panic/active poll: keeps the attending banner fresh while the
    // resident keeps the page open.
    const interval = setInterval(fetchAlerts, 15000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Lane D: prefer the unit bound to the active role/context row (unit_id);
  // fall back to condo scoping when there is no unit.
  const activeCtx = contexts?.find((c) => c.id === activeContextId);
  const unitId =
    activeCtx?.unitId ||
    (user as any)?.unitId ||
    (user as any)?.unit_id ||
    (roles || []).find((r) => r.unitId)?.unitId ||
    undefined;
  const condoId =
    user?.condoId || (user as any)?.condo_id || localStorage.getItem('condoId') || undefined;

  const handleCall = (phone: string) => {
    window.location.href = `tel:${String(phone).replace(/[^0-9+]/g, '')}`;
  };

  const handleTrigger = async () => {
    if (!selectedType) return;
    setStage('sending');
    setError(null);
    try {
      const position = await new Promise<GeolocationPosition | null>((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(resolve, () => resolve(null), { enableHighAccuracy: true, timeout: 4000 });
      });
      const res = await accessApi.panic.trigger({
        kind: selectedType === 'ascensor' ? 'stuck_elevator' : 'panic',
        unitId: unitId || undefined,
        condoId: unitId ? undefined : condoId,
        alertType: selectedType,
        alert_type: selectedType,
        type: selectedType,
        locationLat: position?.coords.latitude,
        locationLng: position?.coords.longitude,
        notes: notes.trim() || undefined,
      });
      const payload = res.data?.data ?? res.data;
      const notified = typeof payload?.notified === 'number' ? payload.notified : null;
      setNotifiedCount(notified);
      setStage('sent');
      fetchAlerts();
    } catch (err: any) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code || err?.response?.data?.error?.code;
      if (status === 409 || code === 'PANIC_DUPLICATE') {
        // Informational, not an error: there is already an active alert.
        showToast(t('panic.duplicateToast', 'Ya hay una alerta activa tuya'));
        setStage('select');
      } else {
        setError(err?.response?.data?.message || err?.message || 'Error al enviar alerta');
        setStage('select');
      }
    }
  };

  const handleRespond = async (id: string) => {
    setActionId(id);
    try {
      await accessApi.panic.respond(id);
      fetchAlerts();
    } catch {
      // ignore
    } finally {
      setActionId(null);
    }
  };

  const handleResolve = async (id: string) => {
    if (!confirm('¿Marcar esta alerta como resuelta?')) return;
    setActionId(id);
    try {
      await accessApi.panic.resolve(id);
      fetchAlerts();
    } catch {
      // ignore
    } finally {
      setActionId(null);
    }
  };

  const reset = () => {
    setStage('idle');
    setSelectedType(null);
    setNotes('');
    setError(null);
    setNotifiedCount(null);
  };

  // Resident attending banner: their own alert flipped to 'attending'.
  const myAlertAttending =
    !isAdminOrSecurity &&
    alerts.some((a) => a.status === 'attending' || a.status === 'responded');

  return (
    <div className="min-h-full bg-[var(--v-base-bg)] p-4 md:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Botón de Pánico</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Envía una alerta de emergencia</p>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
            <AlertTriangle className="w-5 h-5" />
            <span className="text-sm">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Resident: attending banner */}
        {myAlertAttending && (
          <div className="flex items-center gap-2 bg-amber-100 dark:bg-amber-500/20 border border-amber-300 dark:border-amber-500/40 text-amber-800 dark:text-amber-300 px-4 py-3 rounded-lg">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm font-medium">{t('panic.attendingBanner', 'Personal de seguridad está atendiendo tu alerta')}</span>
          </div>
        )}

        {/* Panic Button Section */}
        <div className="bg-[var(--v-surface-1)] rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-8">
          {stage === 'idle' && (
            <div className="flex flex-col items-center gap-6">
              <p className="text-center text-slate-600 dark:text-slate-300 max-w-md">
                Mantén presionado para enviar una alerta de emergencia a seguridad y administración
              </p>
              <button
                onClick={() => setStage('select')}
                className="w-48 h-48 rounded-full bg-gradient-to-b from-red-500 to-red-700 hover:from-red-600 hover:to-red-800 shadow-lg shadow-red-200 dark:shadow-red-900/50 hover:shadow-xl hover:shadow-red-300 dark:hover:shadow-red-900/60 transition-all flex items-center justify-center group active:scale-95"
              >
                <div className="flex flex-col items-center gap-2 text-white">
                  <AlertTriangle className="w-16 h-16 group-hover:scale-110 transition-transform" />
                  <span className="text-xl font-bold uppercase tracking-wide">Pánico</span>
                </div>
              </button>
            </div>
          )}

          {stage === 'select' && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Selecciona el tipo de emergencia</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Esta acción notificará inmediatamente</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {alertTypes.map(({ value, label, icon: Icon, color, bg }) => (
                  <button
                    key={value}
                    onClick={() => setSelectedType(value)}
                    className={`flex flex-col items-center gap-3 p-6 rounded-xl border-2 transition-all ${
                      selectedType === value
                        ? 'border-red-500 bg-red-50 dark:bg-red-500/10 scale-105'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                    }`}
                  >
                    <div className={`w-14 h-14 rounded-full ${bg} flex items-center justify-center`}>
                      <Icon className={`w-7 h-7 ${color}`} />
                    </div>
                    <span className="font-medium text-slate-900 dark:text-slate-100">{label}</span>
                  </button>
                ))}
              </div>

              {selectedType && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notas (opcional)</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="Describe la emergencia..."
                    />
                  </div>
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={handleTrigger}
                      disabled={(stage as string) === 'sending'}
                      className="flex items-center gap-2 bg-red-600 hover:bg-red-700 active:scale-95 disabled:opacity-50 disabled:active:scale-100 text-white px-8 py-3 rounded-lg font-bold transition-colors"
                    >
                      {(stage as string) === 'sending' ? (
                        <><Loader2 className="w-5 h-5 animate-spin" /> Enviando...</>
                      ) : (
                        <><AlertTriangle className="w-5 h-5" /> Confirmar Alerta</>
                      )}
                    </button>
                    <button
                      onClick={reset}
                      className="px-6 py-3 border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-100 dark:hover:bg-slate-700"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {stage === 'sent' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Alerta enviada</h2>
              <p className="text-slate-500 dark:text-slate-400 text-center max-w-sm">
                Tu alerta de <span className="font-semibold capitalize">
                  {alertTypes.find((tp) => tp.value === selectedType)?.label}
                </span> ha sido enviada. Seguridad ha sido notificada.
              </p>
              {notifiedCount != null && (
                <p className="text-sm font-medium text-green-700 dark:text-green-300">
                  {t('panic.notified', { count: notifiedCount, defaultValue: `Alerta enviada — ${notifiedCount} vecinos notificados` })}
                </p>
              )}
              <button
                onClick={reset}
                className="mt-4 px-6 py-2 border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                Cerrar
              </button>
            </div>
          )}
        </div>

        {/* Emergency Contacts (visible to everyone) */}
        <div className="bg-[var(--v-surface-1)] rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-red-600 dark:text-red-400" />
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Contactos de emergencia</h2>
            </div>
            <button
              onClick={fetchContacts}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
            >
              Actualizar
            </button>
          </div>

          {contactsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500 dark:text-blue-400" />
            </div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-8">
              <Phone className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
              <p className="text-slate-500 dark:text-slate-400 text-sm">No hay contactos de emergencia registrados</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {contacts.map((contact) => {
                const cfg = getContactConfig(contact.category);
                const CIcon = cfg.icon;
                return (
                  <div
                    key={contact.id}
                    className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/60 rounded-lg p-4 border border-slate-200 dark:border-slate-700"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-10 h-10 rounded-lg ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
                        <CIcon className={`w-5 h-5 ${cfg.color}`} />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 dark:text-slate-100 truncate">{contact.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{cfg.label}</p>
                        <p className="text-sm text-slate-600 dark:text-slate-300">{contact.phone}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCall(contact.phone)}
                      className="flex items-center gap-1 text-sm bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg font-medium flex-shrink-0"
                    >
                      <Phone className="w-3 h-3" /> Llamar
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Admin/Security: Active Alerts */}
        {isAdminOrSecurity && (
          <div className="bg-[var(--v-surface-1)] rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Alertas activas</h2>
              <button
                onClick={fetchAlerts}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
              >
                Actualizar
              </button>
            </div>

            {alertsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500 dark:text-blue-400" />
              </div>
            ) : alerts.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-green-300 dark:text-green-700 mx-auto mb-2" />
                <p className="text-slate-500 dark:text-slate-400 text-sm">No hay alertas activas</p>
              </div>
            ) : (
              <div className="space-y-3">
                {alerts.map((alert) => {
                  const typeConfig =
                    alertTypes.find((tp) => tp.value === alert.alertType) ||
                    alertTypes.find((tp) => tp.value === 'otro')!;
                  const Icon = typeConfig.icon;
                  return (
                    <div
                      key={alert.id}
                      className="flex items-start justify-between bg-slate-50 dark:bg-slate-800/60 rounded-lg p-4 border border-slate-200 dark:border-slate-700"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg ${typeConfig.bg} flex items-center justify-center flex-shrink-0`}>
                          <Icon className={`w-5 h-5 ${typeConfig.color}`} />
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900 dark:text-slate-100 capitalize">{typeConfig.label}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[alert.status]}`}>
                              {statusLabels[alert.status]}
                            </span>
                          </div>
                          {alert.userName && (
                            <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                              <User className="w-3 h-3" /> {alert.userName}
                              {alert.unit && <span>· {alert.unit}</span>}
                            </div>
                          )}
                          {alert.notes && <p className="text-sm text-slate-600 dark:text-slate-300">{alert.notes}</p>}
                          <div className="flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
                            <Clock className="w-3 h-3" />
                            {formatDateTime(alert.createdAt)}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        {alert.status === 'active' && (
                          <button
                            onClick={() => handleRespond(alert.id)}
                            disabled={actionId === alert.id}
                            className="flex items-center gap-1 text-sm bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1.5 rounded-lg font-medium disabled:opacity-50"
                          >
                            {actionId === alert.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronRight className="w-3 h-3" />}
                            Atender
                          </button>
                        )}
                        {alert.status !== 'resolved' && (
                          <button
                            onClick={() => handleResolve(alert.id)}
                            disabled={actionId === alert.id}
                            className="flex items-center gap-1 text-sm bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg font-medium disabled:opacity-50"
                          >
                            <CheckCircle className="w-3 h-3" />
                            Resolver
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Informational toast (PANIC_DUPLICATE etc.) — never styled as an error */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-slate-900 dark:bg-slate-700 text-white px-4 py-3 rounded-lg shadow-lg">
          <Info className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{toast}</span>
        </div>
      )}
    </div>
  );
}
